"use client";

import React, { useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MmIcon } from "@/components/mini-marvel/common/icon";

/**
 * Sign-in for the back-office.
 *
 * Credentials are posted to /api/session and exchanged there; the password
 * never touches client state beyond this form's own input, and the resulting
 * session cookie is httpOnly.
 */
export default function MmSignInPage() {
  const router = useRouter();
  const params = useSearchParams();
  const next = params.get("next") || "/";

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const response = await fetch("/api/session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      const payload = await response.json();
      if (!response.ok) {
        setError(payload.error ?? "Sign-in failed.");
        return;
      }
      router.replace(next);
      router.refresh();
    } catch {
      setError("Could not reach the back-office. Is the Next.js server running?");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-[70vh] w-full max-w-sm flex-col justify-center">
      <div className="mb-6 flex items-center gap-3">
        <div className="grid size-10 place-items-center rounded-xl bg-primary/10 text-primary">
          <MmIcon name="Baby" className="size-5" />
        </div>
        <div>
          <h1 className="text-lg font-semibold leading-tight">Mini Marvel</h1>
          <p className="text-sm text-muted-foreground">Store back-office</p>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-4 rounded-xl border border-border/60 bg-card p-5">
        <div className="space-y-1.5">
          <Label htmlFor="mm-username">Nexus user</Label>
          <Input
            id="mm-username"
            autoComplete="username"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            placeholder="admin"
            required
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="mm-password">Password</Label>
          <Input
            id="mm-password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
        </div>

        {error ? (
          <p className="flex items-start gap-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            <MmIcon name="TriangleAlert" className="mt-0.5 size-4 shrink-0" />
            <span>{error}</span>
          </p>
        ) : null}

        <Button type="submit" className="w-full" disabled={busy}>
          {busy ? <MmIcon name="Loader2" className="mr-2 size-4 animate-spin" /> : null}
          Sign in
        </Button>

        <p className="text-center text-xs text-muted-foreground">
          Signs in against the <code className="font-mono">az-commerce</code> database.
        </p>
      </form>
    </div>
  );
}
